import React from "react";
import { useTranslation } from 'react-i18next';
import Button from "./ui/button";
import axios from "axios";
import { getTestsCount } from '../utils/testUtils';

const NodeDetailsPanel = ({ selectedNode, loading, generateTests, setTestScenarios, setLoading, API_BASE_URL, openLabSection, user, fetchNodes, onOpenTestGeneration }) => {
  const { t } = useTranslation();
  
  if (!selectedNode) {
    return <div className="text-gray-400 italic">{t('nodeDetailsPanel.selectNode')}</div>;
  }

  // ✨ Affichage spécial pour le nœud de bienvenue
  if (selectedNode.name === '__welcome__') {
    return (
      <div className="p-4">
        <h3 className="text-lg font-bold mb-4 text-purple-600">👋 {t('graphView.emptyState.welcome', 'Bienvenue !')}</h3>
        <div className="space-y-3 text-gray-700">
          <p className="font-semibold">{t('graphView.emptyState.intro', 'Le graphe représente votre architecture applicative.')}</p>
          
          <div className="bg-blue-50 p-3 rounded">
            <p className="font-medium">📦 {t('graphView.emptyState.nodes', 'Les NŒUDS')}</p>
            <p className="text-sm ml-4">{t('graphView.emptyState.nodesDesc', 'Représentent vos composants (API, UI, services, bases de données...)')}</p>
          </div>
          
          <div className="bg-green-50 p-3 rounded">
            <p className="font-medium">🔗 {t('graphView.emptyState.relations', 'Les RELATIONS')}</p>
            <p className="text-sm ml-4">{t('graphView.emptyState.relationsDesc', 'Montrent comment vos composants interagissent entre eux')}</p>
          </div>
          
          <div className="bg-yellow-50 p-3 rounded">
            <p className="font-medium">🧪 {t('graphView.emptyState.tests', 'Les TESTS')}</p>
            <p className="text-sm ml-4">{t('graphView.emptyState.testsDesc', 'Valident le bon fonctionnement de vos composants')}</p>
          </div>
          
          <div className="mt-4 p-3 bg-purple-50 rounded border-l-4 border-purple-500">
            <p className="font-semibold text-purple-700">💡 {t('graphView.emptyState.cta', 'Créez votre premier nœud pour commencer !')}</p>
          </div>
        </div>
      </div>
    );
  }

  // Fonction simple pour ouvrir la modal tests ET déclencher l'auto-chargement
  const handleOpenTestsWithAutoLoad = () => {
    // Ouvrir la modal tests avec auto-chargement activé
    if (openLabSection) {
      openLabSection('tests', true); // true = autoLoad
    }
  };

  // Fonction pour ouvrir la modal de génération de tests
  const handleOpenGenerationModal = () => {
    if (selectedNode && onOpenTestGeneration) {
      onOpenTestGeneration();
    }
  };

  return (
    <div>
      <h3 className="text-lg font-bold mb-2">{selectedNode.name}</h3>
      <p className="mb-2">{selectedNode.description}</p>
      <div className="mb-2"><strong>{t('nodeDetailsPanel.type')} :</strong> {selectedNode.type}</div>
      <div className="mb-2"><strong>{t('nodeDetailsPanel.technology')} :</strong> {selectedNode.technology}</div>
      <div className="mb-2"><strong>{t('nodeDetailsPanel.status')} :</strong> {selectedNode.status}</div>
      <div className="mb-2">
        <strong>{t('nodeDetailsPanel.metadata')} :</strong>
        <pre className="bg-gray-100 p-2 rounded">
          {JSON.stringify(selectedNode.metadata, null, 2)}
        </pre>
      </div>
      <div className="mb-2">
        <strong>{t('nodeDetailsPanel.endpointSchema')} :</strong>
        <pre className="bg-gray-100 p-2 rounded">
          {JSON.stringify(selectedNode.endpointSchema, null, 2)}
        </pre>
      </div>
      <div className="mb-2">
        <strong>{t('nodeDetailsPanel.tests')} :</strong>
        {getTestsCount(selectedNode.testsJson) > 0 ? (
          <span className="ml-2 flex items-center">
            <button
              onClick={handleOpenTestsWithAutoLoad}
              disabled={loading}
              className="text-blue-600 hover:text-blue-800 flex items-center cursor-pointer bg-transparent border-none underline"
            >
              <span>
                {t('nodeDetailsPanel.testsAvailable', { count: getTestsCount(selectedNode.testsJson) })}
              </span>
            </button>
          </span>
        ) : (
          <div
            className="mt-2 flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer hover:bg-amber-100 transition-colors"
            onClick={!loading ? handleOpenGenerationModal : undefined}
            role="button"
          >
            <span className="text-2xl leading-none mt-0.5">🧪</span>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-amber-800 text-sm">{t('nodeDetailsPanel.noTests')}</div>
              <div className="text-amber-600 text-xs mt-0.5">Protégez ce nœud avec des scénarios de test générés par IA</div>
            </div>
            <Button
              onClick={(e) => { e.stopPropagation(); handleOpenGenerationModal(); }}
              disabled={loading}
              className="shrink-0 py-1.5 px-3 text-xs bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold shadow-sm whitespace-nowrap"
            >
              🤖 {t('nodeDetailsPanel.generate')} →
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NodeDetailsPanel;